import React from "react";
import { Avatar as MuiAvatar } from "@mui/material";
import { useTheme } from "../theme/useTheme";
import { brand } from "../theme/theme";
import PropTypes from "prop-types";

// Función para generar iniciales a partir del nombre
const getInitials = (name) => {
  if (!name) return "";
  const names = name.trim().split(" ");
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

export const Avatar = ({
  children,
  name,
  src,
  size = "default",
  sx = {},
  className,
  ...props
}) => {
  const { neutrals } = useTheme();
  const hasImage = Boolean(src);

  // Definir tamaños
  const sizeStyles = {
    sm: { width: "24px", height: "24px", fontSize: "0.625rem" },
    default: { width: "32px", height: "32px", fontSize: "0.75rem" },
    lg: { width: "64px", height: "64px", fontSize: "1.25rem" },
  };

  // Handle className sizing (retrocompatibilidad)
  const classNameSizeStyles = className?.includes("h-10 w-10")
    ? { width: "40px", height: "40px", fontSize: "0.875rem" }
    : className?.includes("h-6 w-6")
    ? { width: "24px", height: "24px", fontSize: "0.625rem" }
    : className?.includes("h-16 w-16")
    ? { width: "64px", height: "64px", fontSize: "1.25rem" }
    : {};

  const finalSizeStyles =
    Object.keys(classNameSizeStyles).length > 0
      ? classNameSizeStyles
      : sizeStyles[size] || sizeStyles.default;

  return (
    <MuiAvatar
      src={src}
      sx={{
        ...finalSizeStyles,
        backgroundColor: hasImage ? neutrals.gray[200] : brand.avatar,
        color: hasImage ? neutrals.gray[600] : "#fff",
        fontWeight: 500,
        border: `1px solid ${
          hasImage ? neutrals.gray[300] : brand.avatar
        }`,
        ...sx,
      }}
      {...props}
    >
      {children || (name ? getInitials(name) : "")}
    </MuiAvatar>
  );
};

Avatar.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string,
  src: PropTypes.string,
  size: PropTypes.oneOf(["sm", "default", "lg"]),
  className: PropTypes.string,
};

export const AvatarImage = ({ src, alt, sx = {}, ...props }) => {
  return (
    <img
      src={src}
      alt={alt}
      style={{
        aspectRatio: "1",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        ...sx,
      }}
      {...props}
    />
  );
};

export const AvatarFallback = ({ children, sx = {}, className, ...props }) => {
  const { theme } = useTheme();

  // Handle className colors
  const colorStyles = className?.includes("bg-primary text-primary-foreground")
    ? {
        backgroundColor: brand.avatar,
        color: "#fff",
      }
    : {
        backgroundColor: theme.palette.action?.hover || theme.palette.grey[100],
        color: theme.palette.text.primary,
      };

  return (
    <MuiAvatar
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        fontSize: "0.875rem",
        fontWeight: 500,
        ...colorStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiAvatar>
  );
};
